import {
  getSnackOrderDetailsOptions,
  snackMenuItemsOptions,
  snackSupplierOptions,
  updateSnackOrder,
  updateSnackOrderStatus,
  useBuilding,
  useSnackOrderDetails,
  userOptions,
} from "@/api";
import { useWelcomeSnackAccess } from "@/hooks/useWelcomeSnackAccess";
import {
  Breadcrumbs,
  Button,
  Icon,
  InputField,
  LoadingSpinner,
  PopupCard,
  StrictNumberInput,
} from "@app-components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Suspense, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import styles from "@/styles/Snack.module.scss";
import PageBackButton from "@/components/PageBackButton";
import {
  firebaseTimestampToDate,
  hasCustomErrorCode,
  isSnackOrderDeliveryMethod,
  snackStatusColorMap,
  getBuildingId,
  isSnackOrderWithServiceType,
} from "@/lib";
import {
  PrintableSnackOrder,
  NotificationType,
  SnackOrderStatus,
  SnackOrderStatusUpdate,
  SnackOrderEditResult,
  SnackOrderFormType,
  SnackOrder,
  SnackMenu,
} from "@/types";
import CartSummary from "@/components/snack/CartSummary";
import PriceBreakdown from "@/components/snack/PriceBreakdown";
import {
  formatTimeUntil,
  SNACK_ORDER_PRINT_STYLES,
  DATE_TIME_LOCALE,
} from "@/lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouterStore, useSnackbarStore } from "@/store/zustandStore";
import { useReactToPrint } from "react-to-print";
import { PrintableOrders } from "@/components/snack/PrintableOrders";
import { useForm, useStore } from "@tanstack/react-form";
import { usePrompt } from "@/hooks/usePrompt";
import { useConfirm } from "@/hooks/useConfirm";
import SaveResetButtons from "@/components/SaveResetButtons";
import DeliveryTimeInputs from "@/components/snack/order_form/DeliveryTimeInputs";
import OrderHistory from "@/components/snack/OrderHistory";
import _ from "lodash";
import { useBuildingQuery } from "@/components/RouterSetup";
import { OrderReadError } from "@/components/snack/OrderReadError";
import { OrderLifecycleSummary } from "@/components/snack/OrderLifecycleSummary";
import { snackOrderStatusKey } from "@/lib/snackOrderLifecycle";
import { reconcileSupplierOrderList } from "@/lib/snackOrderListCache";

export const Route = createFileRoute("/_portal/snack-admin/orders/$orderId")({
  component: RouteComponent,
  loader: ({ params }) => {
    const { queryClient, supplierCompanyId } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.prefetchQuery(snackSupplierOptions(supplierCompanyId));
    queryClient.prefetchQuery(
      getSnackOrderDetailsOptions(supplierCompanyId, params.orderId),
    );
    queryClient.prefetchQuery(snackMenuItemsOptions(supplierCompanyId, true));
  },
});

function RouteComponent() {
  const { allowSnackAdminAccess } = useWelcomeSnackAccess();

  if (!allowSnackAdminAccess) return;

  return (
    <Suspense
      fallback={
        <div className="center">
          <LoadingSpinner />
        </div>
      }
    >
      <AdminOrderDetails />
    </Suspense>
  );
}

type OrderChange = {
  icon?: string;
  label: string;
  oldDisplay: string;
  newDisplay: string;
};
type OrderChangeSummary = {
  fieldChanges: OrderChange[];
  itemChanges: OrderChange[];
};

function buildOrderChangeSummary(
  order: SnackOrder,
  value: SnackOrderFormType,
  menu: SnackMenu | null | undefined,
  lang: string,
  t: (key: string) => string,
): OrderChangeSummary {
  const fieldChanges: OrderChange[] = [];
  const itemChanges: OrderChange[] = [];
  const locale = lang === "en" ? "en" : "no";
  const fmt = DATE_TIME_LOCALE(lang);

  if (value.deliveryType && order.deliveryType !== value.deliveryType) {
    fieldChanges.push({
      icon: isSnackOrderDeliveryMethod(value.deliveryType)
        ? "meal_delivery"
        : "shopping_bag",
      label: t("SNACK_ADMIN.PRINT.SERVICE"),
      oldDisplay: isSnackOrderWithServiceType(order.deliveryType)
        ? t(`SNACK_ADMIN.SETTINGS.${order.deliveryType.toUpperCase()}`)
        : t(
            `PAGES.ORDER_FOOD.DELIVERY_METHOD.${order.deliveryType.toUpperCase()}.LABEL`,
          ),
      newDisplay: t(`SNACK_ADMIN.SETTINGS.${value.deliveryType.toUpperCase()}`),
    });
  }

  const oldTime = firebaseTimestampToDate(order.deliveryTime);
  if (oldTime.getTime() !== value.deliveryTime.getTime()) {
    fieldChanges.push({
      icon: "date_range",
      label: t("SNACK_ADMIN.PRINT.TIME.DELIVERY"),
      oldDisplay: fmt.format(oldTime),
      newDisplay: fmt.format(value.deliveryTime),
    });
  }

  if (order.participantsCount !== value.participantsCount) {
    fieldChanges.push({
      icon: "groups",
      label: t("SNACK_ADMIN.ORDER_DETAILS.PARTICIPANTS_COUNT"),
      oldDisplay: String(order.participantsCount ?? "—"),
      newDisplay: String(value.participantsCount ?? "—"),
    });
  }

  if ((order.invoiceReference ?? "") !== value.invoiceReference) {
    fieldChanges.push({
      icon: "receipt",
      label: t("BOOKINGS.MODAL.LABELS.INVOICE_REFERENCE"),
      oldDisplay: _.isObject(order.invoiceReference)
        ? Object.values(order.invoiceReference).join(", ")
        : order.invoiceReference?.trim() || "—",
      newDisplay: _.isObject(value.invoiceReference)
        ? Object.values(value.invoiceReference).join(", ")
        : value.invoiceReference.trim() || "—",
    });
  }

  const getItemName = (menuItemId: string): string =>
    order.items.find((i) => i.id === menuItemId)?.name[locale] ??
    menu?.items.find((i) => i.id === menuItemId)?.name[locale] ??
    menuItemId;

  const oldMap = new Map(order.items.map((i) => [i.id, i]));
  const newMap = new Map(value.items.map((i) => [i.menuItemId, i]));

  for (const [id, oldItem] of oldMap) {
    const newItem = newMap.get(id);
    if (!newItem) {
      itemChanges.push({
        label: getItemName(id),
        oldDisplay: `${oldItem.quantity}×`,
        newDisplay: "—",
      });
    } else if (oldItem.quantity !== newItem.quantity) {
      itemChanges.push({
        label: getItemName(id),
        oldDisplay: `${oldItem.quantity}×`,
        newDisplay: `${newItem.quantity}×`,
      });
    }
  }

  for (const [id, newItem] of newMap) {
    if (!oldMap.has(id)) {
      itemChanges.push({
        label: getItemName(id),
        oldDisplay: "—",
        newDisplay: `${newItem.quantity}×`,
      });
    }
  }

  return { fieldChanges, itemChanges };
}

function ChangeRow({
  change,
  subLabel,
}: {
  change: OrderChange;
  subLabel?: boolean;
}) {
  return (
    <p
      style={{
        display: "flex",
        gap: "0px var(--wlcm-spacing-xs)",
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <strong
        style={{
          minWidth: "10rem",
          paddingLeft: subLabel ? "var(--wlcm-spacing-md)" : 0,
          ...(subLabel ? { fontWeight: "normal" } : {}),
        }}
      >
        {change.icon && (
          <Icon
            name={change.icon}
            style={{
              verticalAlign: "text-top",
              marginRight: "var(--wlcm-spacing-xs)",
            }}
          />
        )}
        {change.label}
      </strong>
      <span style={{ textDecoration: "line-through", opacity: 0.5 }}>
        {change.oldDisplay}
      </span>
      <Icon name="arrow_forward" style={{ fontSize: "var(--wlcm-text-sm)" }} />
      <span
        style={{ fontWeight: 600, color: "var(--wlcm-color-success-green)" }}
      >
        {change.newDisplay}
      </span>
    </p>
  );
}

function OrderChangesSummary({
  summary,
  cartLabel,
}: {
  summary: OrderChangeSummary;
  cartLabel: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--wlcm-spacing-sm)",
        marginTop: "var(--wlcm-spacing-sm)",
      }}
    >
      {summary.fieldChanges.map((change, i) => (
        <ChangeRow key={i} change={change} />
      ))}
      {summary.itemChanges.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--wlcm-spacing-xs)",
          }}
        >
          <h4>
            <Icon
              name={"shopping_cart"}
              style={{
                verticalAlign: "text-top",
                marginRight: "var(--wlcm-spacing-xs)",
              }}
            />
            {cartLabel}
          </h4>
          {summary.itemChanges.map((change, i) => (
            <ChangeRow key={i} change={change} subLabel />
          ))}
        </div>
      )}
    </div>
  );
}

function AdminOrderDetails() {
  const { orderId } = Route.useParams();
  const { data: building } = useBuilding();
  const {
    data: order,
    error,
    isPending,
    isFetching,
    refetch,
  } = useSnackOrderDetails(building?.supplierCompanyId, orderId);
  if (!order) {
    return error ? (
      <OrderReadError retry={refetch} isFetching={isFetching} hasData={false} />
    ) : isPending ? (
      <LoadingSpinner />
    ) : null;
  }
  return (
    <LoadedAdminOrderDetails
      key={order.id}
      order={order}
      readError={error}
      isFetching={isFetching}
      retry={refetch}
    />
  );
}

function orderFormValues(order: SnackOrder): SnackOrderFormType {
  return {
    ...order,
    invoiceReference: order.invoiceReference ?? "",
    deliveryTime: firebaseTimestampToDate(order.deliveryTime),
    items: order.items.map((item) => ({ ...item, menuItemId: item.id })),
  };
}

function LoadedAdminOrderDetails({
  order,
  readError,
  isFetching,
  retry,
}: {
  order: SnackOrder;
  readError: unknown;
  isFetching: boolean;
  retry: () => unknown;
}) {
  const { t, i18n } = useTranslation();
  const { orderId } = Route.useParams();
  const queryClient = useQueryClient();
  const { addNotification } = useSnackbarStore();
  const { data: building } = useBuilding();
  const { data: supplier } = useBuildingQuery(
    snackSupplierOptions(building?.supplierCompanyId),
  );
  const { data: orderUser } = useBuildingQuery(userOptions(order?.userId));
  const { data: menu } = useBuildingQuery(
    snackMenuItemsOptions(building?.supplierCompanyId, true),
  );
  const navigate = useNavigate();
  const printComponentRef = useRef<HTMLDivElement>(null);
  const breadcrumbs = [
    { label: t("PAGES.SNACK_ADMIN.HEADER"), path: "/snack-admin/orders" },
    {
      label: `#${order?.orderNumber ?? ""}`,
      path: `/snack-admin/orders/${orderId}`,
    },
  ];
  const statusColor = order
    ? order.status.current === SnackOrderStatus.ORDERED
      ? "var(--wlcm-color-warning-orange)"
      : snackStatusColorMap[order.status.current]
    : "";
  // PAY-505: an absent or "legacy" mode keeps the existing flow. In the active
  // lifecycle the server's advisory capabilities drive the actions; every
  // mutation is re-checked by the backend against current state.
  const isActiveLifecycle = order?.lifecycleMode === "delivery-closure-v1";
  const canEdit = order
    ? order.capabilities
      ? order.capabilities.canEdit
      : order.status.current !== SnackOrderStatus.COMPLETED
    : false;
  // Which payload the Delivered/Picked-up button sends: DELIVERED only when the
  // active backend says marking is possible; the old COMPLETED write otherwise.
  const markDeliveredStatus: SnackOrderStatus | null = !isActiveLifecycle
    ? SnackOrderStatus.COMPLETED
    : order?.capabilities?.canMarkDelivered
      ? SnackOrderStatus.DELIVERED
      : null;
  // Explicit close exists only in the active lifecycle and only at Delivered;
  // the legacy flow closes through the Delivered/Picked-up button instead.
  const canClose =
    isActiveLifecycle &&
    order?.status.current === SnackOrderStatus.DELIVERED &&
    order.capabilities?.canClose === true;
  // The returned deadline is advisory; the backend decides at marking time.
  // Evaluated when staff click, not when the page rendered, so a page left
  // open across the deadline still warns.
  const closureDeadlineHasPassed = () =>
    isActiveLifecycle &&
    typeof order?.automaticCloseAt === "string" &&
    Date.parse(order.automaticCloseAt) <= Date.now();

  const [showDenyModal, setShowDenyModal] = useState(false);
  const [denyReason, setDenyReason] = useState("");

  const [editMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Refresh updates the displayed server state, never an open draft. Each new
  // editing session explicitly starts from the latest fetched/committed order.
  const [initialValues, setInitialValues] = useState(() =>
    orderFormValues(order),
  );
  const form = useForm<SnackOrderFormType>({
    defaultValues: initialValues,
    onSubmit: ({ value }) => {
      const summary = buildOrderChangeSummary(
        order!,
        value,
        menu,
        i18n.language,
        t,
      );
      showConfirm({
        title: t("SNACK_ADMIN.ORDER_DETAILS.SAVE_MODAL.TITLE"),
        subtitle: (
          <>
            <p
              className="banner infoBanner dark"
              style={{ marginTop: "var(--wlcm-spacing-sm)" }}
            >
              <Icon name="info" />
              {t("SNACK_ADMIN.ORDER_DETAILS.SAVE_MODAL.INFO")}
            </p>
            {/* PAY-505: a Delivered order with timed automation stays
                editable until its closure is recorded. Saving may itself
                record that closure when the saved schedule's closing time has
                passed, or be refused when the order closed meanwhile; say so
                before sending. Manual mode has no closing time and keeps the
                existing wording. */}
            {isActiveLifecycle &&
              order?.status.current === SnackOrderStatus.DELIVERED &&
              typeof order.automaticCloseAt === "string" && (
                <p
                  className="banner infoBanner dark"
                  style={{ marginTop: "var(--wlcm-spacing-sm)" }}
                >
                  <Icon name="info" />
                  {t(
                    "SNACK_ADMIN.ORDER_DETAILS.SAVE_MODAL.DELIVERED_MAY_CLOSE",
                  )}
                </p>
              )}
            <OrderChangesSummary
              summary={summary}
              cartLabel={t("PAGES.ORDER_FOOD.CART.HEADER")}
            />
          </>
        ),
        confirmText: t("SAVE_CHANGES"),
        confirmButtonProps: {
          danger: false,
          success: true,
        },
        cancelButtonProps: {
          dark: true,
        },
        onConfirm: async () => {
          setIsSaving(true);
          const entity = t("SNACK_ADMIN.ORDER").toLowerCase();
          try {
            const {
              items,
              deliveryType,
              deliveryTime,
              participantsCount,
              invoiceReference,
            } = value;
            const { data: committed } = await updateSnackOrder(orderId, {
              items,
              deliveryType,
              deliveryTime,
              participantsCount: Number(participantsCount),
              invoiceReference,
            });
            // The backend reports the committed order. When the saved
            // schedule's closing time had already passed, the same
            // transaction also closed the order: show that result at once.
            const closedOnSave =
              committed.status.current === SnackOrderStatus.COMPLETED &&
              order?.status.current !== SnackOrderStatus.COMPLETED;
            await applyCommittedOrder(committed);
            addNotification(
              t("NOTIFICATIONS.DOCUMENT_UPDATED", { entity }),
              NotificationType.SUCCESS,
            );
            if (closedOnSave) {
              addNotification(
                t("PAGES.ORDER_FOOD.NOTIFICATIONS.ORDER_CLOSED_ON_SAVE"),
                NotificationType.INFO,
              );
            }
            invalidateOrder();
            setEditMode(false);
          } catch (error) {
            if (
              hasCustomErrorCode(error) &&
              error.response?.data.code === "ORDER_LIFECYCLE_CONFLICT"
            ) {
              // The stored order no longer allows editing: it closed in
              // the meantime, or this request's own transaction recorded
              // its overdue closure. Keep the draft (edit mode stays on,
              // the form is untouched), explain and show the current order.
              await handleStatusError(error, "NOTIFICATIONS.UPDATE_FAILED");
            } else {
              // Permission and request-shape rejections (Functions layer, or
              // the backend's ownership check) keep their own messages, as
              // before. Other backend rejections now arrive with their own
              // code too, but none has a save-specific translation; show the
              // generic failure message for them.
              const code =
                hasCustomErrorCode(error) && error.response
                  ? error.response.data.code
                  : undefined;
              const keepsOwnMessage =
                code === "FORBIDDEN_INSUFFICIENT_PERMISSIONS" ||
                code === "MISSING_OR_INVALID_PARAMS" ||
                code === "REQUEST_BLOCKED" ||
                code?.startsWith("UNAUTHORIZED") === true;
              addNotification(
                t(
                  keepsOwnMessage
                    ? `NOTIFICATIONS.${code}`
                    : "NOTIFICATIONS.UPDATE_FAILED",
                  { entity },
                ),
              );
            }
          } finally {
            setIsSaving(false);
          }
        },
      });
    },
  });
  const hasUnsavedChanges =
    useStore(form.store, (state) => state.isDirty) && editMode;
  const resetToCurrentOrder = () => {
    const values = orderFormValues(order);
    setInitialValues(values);
    form.reset(values);
  };
  const { show: showConfirm } = useConfirm();
  usePrompt({
    isOpen: hasUnsavedChanges,
  });

  const orderQueryKey = [getBuildingId(), "snack-order-details", orderId];
  const invalidateOrder = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: orderQueryKey }),
      queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "all-orders", building?.supplierCompanyId],
      }),
    ]);

  // Show the committed status at once from the mutation's own projection; the
  // invalidation that follows refetches the full order. A reply without the
  // lifecycle fields (old or legacy backend) clears them, which keeps the old
  // flow.
  const applyCommittedStatus = async (committed: SnackOrderStatusUpdate) => {
    await queryClient.cancelQueries({ queryKey: orderQueryKey });
    const update = (current: SnackOrder | null | undefined) =>
      current && current.id === committed.id
        ? {
            ...current,
            status: {
              ...current.status,
              current: committed.status,
              updatedAt: committed.statusUpdatedAt,
              notes: committed.statusNotes ?? current.status.notes,
            },
            lifecycleMode: committed.lifecycleMode,
            automaticCloseAt: committed.automaticCloseAt,
            capabilities: committed.capabilities,
            lifecycle: committed.lifecycle,
          }
        : current;
    queryClient.setQueryData<SnackOrder | null>(orderQueryKey, update);
    await queryClient.cancelQueries({
      queryKey: [getBuildingId(), "all-orders", building?.supplierCompanyId],
    });
    queryClient.setQueriesData<SnackOrder[]>(
      {
        queryKey: [getBuildingId(), "all-orders", building?.supplierCompanyId],
      },
      (current) => current?.map((row) => update(row)!),
    );
  };

  // An edit answers the committed order projection; merge it the same way,
  // clearing the lifecycle fields a legacy or old backend leaves out.
  const applyCommittedOrder = async (committed: SnackOrderEditResult) => {
    await queryClient.cancelQueries({ queryKey: orderQueryKey });
    const update = (current: SnackOrder | null | undefined) =>
      current && current.id === committed.id
        ? {
            ...current,
            ...committed,
            lifecycleMode: committed.lifecycleMode,
            automaticCloseAt: committed.automaticCloseAt,
            capabilities: committed.capabilities,
            lifecycle: committed.lifecycle,
          }
        : current;
    const updatedOrder =
      queryClient.setQueryData<SnackOrder | null>(orderQueryKey, update) ??
      update(order);
    await queryClient.cancelQueries({
      queryKey: [getBuildingId(), "all-orders", building?.supplierCompanyId],
    });
    if (updatedOrder) {
      const now = new Date();
      for (const [queryKey, current] of queryClient.getQueriesData<
        SnackOrder[]
      >({
        queryKey: [getBuildingId(), "all-orders", building?.supplierCompanyId],
      })) {
        if (!current) continue;
        queryClient.setQueryData(
          queryKey,
          reconcileSupplierOrderList(current, updatedOrder, queryKey[3], now),
        );
      }
    }
  };

  // A lifecycle conflict means the stored order no longer permits the action
  // (already delivered, closed, or a mode mismatch). Explain, show the
  // current order the backend returned and refresh; never retry the
  // mutation automatically.
  const handleStatusError = async (error: unknown, fallbackKey: string) => {
    console.error(error);
    if (
      hasCustomErrorCode(error) &&
      error.response?.data.code === "ORDER_LIFECYCLE_CONFLICT"
    ) {
      const conflict = (
        error.response.data as {
          data?: { reason?: string; order?: SnackOrderStatusUpdate };
        }
      ).data;
      if (conflict?.order?.id) await applyCommittedStatus(conflict.order);
      addNotification(
        t("PAGES.ORDER_FOOD.NOTIFICATIONS.ORDER_LIFECYCLE_CONFLICT"),
      );
      invalidateOrder();
      return;
    }
    addNotification(t(fallbackKey));
  };

  const { mutate: denyOrder, isPending: isDenying } = useMutation({
    mutationFn: ({ statusNotes }: { statusNotes?: string }) =>
      updateSnackOrderStatus(orderId, SnackOrderStatus.DENIED, statusNotes),
    onSuccess: async ({ data: committed }) => {
      addNotification(
        t("PAGES.ORDER_FOOD.NOTIFICATIONS.DENIED"),
        NotificationType.SUCCESS,
      );
      await applyCommittedStatus(committed);
      invalidateOrder();
    },
    onError: (error) =>
      handleStatusError(error, "PAGES.ORDER_FOOD.NOTIFICATIONS.DENIAL_FAILED"),
  });

  const { mutate: updateStatus, isPending: isUpdatingStatus } = useMutation({
    mutationFn: (newStatus: SnackOrderStatus) =>
      updateSnackOrderStatus(orderId, newStatus),
    onSuccess: async ({ data: committed }) => {
      addNotification(
        t("PAGES.ORDER_FOOD.NOTIFICATIONS.STATUS_UPDATED"),
        NotificationType.SUCCESS,
      );
      await applyCommittedStatus(committed);
      invalidateOrder();
    },
    onError: (error) =>
      handleStatusError(
        error,
        "PAGES.ORDER_FOOD.NOTIFICATIONS.STATUS_UPDATE_FAILED",
      ),
  });

  // Explicit close. Closing ends ordinary editing and marks the order ready
  // for invoicing, so confirm first; the backend re-checks the stored state.
  // Suggest corrections only while the server still allows editing: an
  // overdue Delivered order awaiting the worker can be closed but not edited.
  const closeOrder = () =>
    showConfirm({
      title: t("SNACK_ADMIN.ORDER_DETAILS.CLOSE_MODAL.TITLE"),
      subtitle: [
        t("SNACK_ADMIN.ORDER_DETAILS.CLOSE_MODAL.BODY"),
        ...(canEdit
          ? [t("SNACK_ADMIN.ORDER_DETAILS.CLOSE_MODAL.CORRECT_FIRST")]
          : []),
      ].join(" "),
      confirmText: t("SNACK_ADMIN.ORDER_DETAILS.ACTIONS.CLOSE"),
      confirmButtonProps: { danger: false, success: true },
      cancelButtonProps: { dark: true },
      onConfirm: () => updateStatus(SnackOrderStatus.COMPLETED),
    });

  // Delivered/Picked-up. When the returned closure deadline is already past,
  // marking will also close the order in the same backend transaction, so say
  // so before sending.
  const markDelivered = () => {
    if (!markDeliveredStatus) return;
    if (!closureDeadlineHasPassed()) {
      updateStatus(markDeliveredStatus);
      return;
    }
    showConfirm({
      title: t("SNACK_ADMIN.ORDER_DETAILS.MARK_DELIVERED_MODAL.TITLE"),
      subtitle: t("SNACK_ADMIN.ORDER_DETAILS.MARK_DELIVERED_MODAL.BODY"),
      confirmText: t(
        `SNACK_ADMIN.ORDER_DETAILS.ACTIONS.${
          order && isSnackOrderDeliveryMethod(order.deliveryType)
            ? "DELIVERED"
            : "PICKED_UP"
        }`,
      ),
      confirmButtonProps: { danger: false, success: true },
      cancelButtonProps: { dark: true },
      onConfirm: () => updateStatus(markDeliveredStatus),
    });
  };

  const printableOrder = useMemo<PrintableSnackOrder[]>(
    () =>
      !order
        ? []
        : [
            {
              ...order,
              userPhone: orderUser?.phone || "",
            },
          ],
    [order, orderUser],
  );
  const handlePrint = useReactToPrint({
    contentRef: printComponentRef,
    documentTitle: `${order?.orderNumber ?? "order"}.pdf`,
    pageStyle: SNACK_ORDER_PRINT_STYLES,
  });

  return !order ? (
    <></>
  ) : (
    <>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton fallbackPath={{ to: breadcrumbs[0].path }} />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      {!!readError && (
        <OrderReadError retry={retry} isFetching={isFetching} hasData />
      )}
      <div className={styles.orderDetailsContainer}>
        <div className={styles.headerAndActions}>
          <div>
            <h1 className="h2">
              {t("PAGES.MY_ORDERS.ORDER_DETAILS.HEADER", {
                orderNumber: order.orderNumber,
              })}
            </h1>
            {
              <p>
                <span
                  className={styles.statusDot}
                  style={{ background: statusColor }}
                />
                <span>
                  {t(
                    snackOrderStatusKey(
                      order.status.current,
                      order.lifecycleMode,
                    ),
                  )}
                </span>
              </p>
            }
            <OrderLifecycleSummary order={order} />
          </div>
          {editMode ? (
            <div>
              <Button
                secondary
                icon="close"
                disabled={isSaving}
                onClick={() =>
                  showConfirm({
                    title: t("SNACK_ADMIN.ORDER_DETAILS.RESET_MODAL.HEADER"),
                    subtitle: t("SNACK_ADMIN.ORDER_DETAILS.RESET_MODAL.BODY"),
                    confirmText: t("DISCARD_CHANGES"),
                    onConfirm: () => {
                      resetToCurrentOrder();
                      setEditMode(false);
                    },
                  })
                }
              >
                {t("DISCARD_CHANGES")}
              </Button>
            </div>
          ) : (
            <div>
              {canEdit && (
                <Button
                  secondary
                  icon="edit"
                  onClick={() => {
                    resetToCurrentOrder();
                    setEditMode(true);
                  }}
                >
                  {t("SNACK_ADMIN.ORDER_DETAILS.ACTIONS.EDIT")}
                </Button>
              )}
              {
                order.status.current === SnackOrderStatus.ORDERED ? (
                  <>
                    <Button
                      success
                      icon="done"
                      isLoading={isUpdatingStatus}
                      onClick={() => updateStatus(SnackOrderStatus.CONFIRMED)}
                    >
                      {t("SNACK_ADMIN.ORDER_DETAILS.ACTIONS.CONFIRM")}
                    </Button>
                    <Button
                      danger
                      icon="close"
                      isLoading={isDenying}
                      onClick={() => setShowDenyModal(true)}
                    >
                      {t("SNACK_ADMIN.ORDER_DETAILS.ACTIONS.DENY")}
                    </Button>
                  </>
                ) : order.status.current === SnackOrderStatus.CONFIRMED ? (
                  <>
                    <Button
                      success
                      icon="skillet"
                      isLoading={isUpdatingStatus}
                      onClick={() => updateStatus(SnackOrderStatus.PREPARING)}
                    >
                      {t("SNACK_ADMIN.ORDER_DETAILS.ACTIONS.PREPARING")}
                    </Button>
                    <Button
                      danger
                      icon="close"
                      isLoading={isDenying}
                      onClick={() => setShowDenyModal(true)}
                    >
                      {t("SNACK_ADMIN.ORDER_DETAILS.ACTIONS.CANCEL")}
                    </Button>
                  </>
                ) : order.status.current === SnackOrderStatus.PREPARING ? (
                  <>
                    {markDeliveredStatus &&
                      (isSnackOrderDeliveryMethod(order.deliveryType) ? (
                        <Button
                          success
                          icon="meal_delivery"
                          isLoading={isUpdatingStatus}
                          onClick={markDelivered}
                        >
                          {t("SNACK_ADMIN.ORDER_DETAILS.ACTIONS.DELIVERED")}
                        </Button>
                      ) : (
                        <Button
                          success
                          icon="shopping_bag"
                          isLoading={isUpdatingStatus}
                          onClick={markDelivered}
                        >
                          {t("SNACK_ADMIN.ORDER_DETAILS.ACTIONS.PICKED_UP")}
                        </Button>
                      ))}
                  </>
                ) : canClose ? (
                  <Button
                    success
                    icon="check_circle"
                    isLoading={isUpdatingStatus}
                    onClick={closeOrder}
                  >
                    {t("SNACK_ADMIN.ORDER_DETAILS.ACTIONS.CLOSE")}
                  </Button>
                ) : null /* Delivered and Closed offer no Deny/Cancel */
              }
            </div>
          )}
        </div>
        <div className={styles.columns}>
          <div>
            <CartSummary
              {...(editMode
                ? { form, order, adminMode: true, showAddItemsInput: true }
                : { order })}
            />
            {editMode ? (
              <PriceBreakdown order={order} snackAdminMode form={form} />
            ) : (
              <PriceBreakdown order={order} snackAdminMode />
            )}
          </div>
          <div>
            <div className={styles.summaryCard}>
              <h3>{t("DETAILS")}</h3>
              {order.status.current === SnackOrderStatus.CANCELLED && (
                <p className={styles.orderDetail}>
                  <Icon name="close" />
                  <p>{t("SNACK_ADMIN.ORDER_DETAILS.CANCELLED_BY_USER")}</p>
                </p>
              )}
              {order.status.current === SnackOrderStatus.DENIED && (
                <p className={styles.orderDetail}>
                  <Icon name="close" className="dangerIcon" />
                  <p>
                    {t("SNACK_ADMIN.ORDER_DETAILS.DENIED_BY_ADMIN")}
                    {order.status.notes && (
                      <>
                        {" "}
                        <Trans
                          i18nKey="SNACK_ADMIN.ORDER_DETAILS.WITH_REASON"
                          values={{ reason: order.status.notes }}
                          components={{ itallic: <i></i> }}
                        />
                      </>
                    )}
                  </p>
                </p>
              )}
              <div>
                {editMode ? (
                  <div
                    className={styles.orderDetail}
                    style={{ alignItems: "center", flexWrap: "wrap" }}
                  >
                    <form.Subscribe
                      selector={({ values }) => values.deliveryType}
                      children={(deliveryType) => (
                        <Icon
                          name={
                            isSnackOrderDeliveryMethod(
                              (deliveryType || order.deliveryType) as string,
                            )
                              ? "meal_delivery"
                              : "shopping_bag"
                          }
                        />
                      )}
                    />
                    <DeliveryTimeInputs
                      form={form}
                      adminMode
                      editableDeliveryType
                    />
                  </div>
                ) : (
                  <p className={styles.orderDetail}>
                    <Icon
                      name={
                        isSnackOrderDeliveryMethod(order.deliveryType)
                          ? "meal_delivery"
                          : "shopping_bag"
                      }
                    />
                    <span>
                      <Trans
                        i18nKey={`SNACK_ADMIN.ORDER_DETAILS.DELIVERY_INFO.${order.deliveryType.toUpperCase()}`}
                        values={{
                          time: formatTimeUntil(
                            order.deliveryTime,
                            t,
                            i18n.language,
                          ),
                        }}
                        components={{
                          bold: (
                            <strong
                              style={
                                [
                                  SnackOrderStatus.DENIED,
                                  SnackOrderStatus.CANCELLED,
                                ].includes(order.status.current)
                                  ? { textDecoration: "line-through" }
                                  : { color: statusColor }
                              }
                            ></strong>
                          ),
                        }}
                      />
                    </span>
                  </p>
                )}
                {order.deliveryInstructions && (
                  <p
                    className={`${styles.orderDetail} ${styles.subOrderDetail}`}
                  >
                    <Icon
                      name={
                        isSnackOrderDeliveryMethod(order.deliveryType)
                          ? "location_on"
                          : "comment"
                      }
                    />
                    <span>
                      <i>"{order.deliveryInstructions}"</i>
                    </span>
                  </p>
                )}
              </div>
              <div>
                <p className={styles.orderDetail}>
                  <Icon name="person" />
                  <span>
                    {t("ACCESS_CARD.TABLE_HEADERS.ORDERED_BY")}{" "}
                    <strong>
                      {order.userFirstName} {order.userLastName}
                    </strong>
                  </span>
                </p>
                {!!orderUser?.phone && (
                  <p
                    className={`${styles.orderDetail} ${styles.subOrderDetail}`}
                  >
                    <Icon name="phone_android" />
                    <a href={`tel:${orderUser?.phone}`}>{orderUser?.phone}</a>
                  </p>
                )}
                <p className={`${styles.orderDetail} ${styles.subOrderDetail}`}>
                  <Icon name="mail" />
                  <a
                    href={`mailto:${orderUser ? orderUser.email : order.userEmail}`}
                  >
                    {orderUser ? orderUser.email : order.userEmail}
                  </a>
                </p>
                <p className={`${styles.orderDetail} ${styles.subOrderDetail}`}>
                  <Icon name="work" />
                  <span>{order.companyName}</span>
                </p>
              </div>
              {editMode ? (
                <div
                  className={styles.orderDetail}
                  style={{ alignItems: "center" }}
                >
                  <Icon name="restaurant" />
                  <p>{t("SNACK_ADMIN.ORDER_DETAILS.PARTICIPANTS_COUNT")}:</p>
                  <form.Field name="participantsCount">
                    {(field) => (
                      <StrictNumberInput
                        required
                        compact
                        label={t(
                          "PAGES.ORDER_FOOD.CHECKOUT.PARTICIPANTS_COUNT.LABEL",
                        )}
                        hideLabel
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        min={1}
                        max={10_000}
                      />
                    )}
                  </form.Field>
                </div>
              ) : (
                <p className={styles.orderDetail}>
                  <Icon name="restaurant" />
                  <span>
                    {t("SNACK_ADMIN.ORDER_DETAILS.PARTICIPANTS_COUNT")}:{" "}
                    <strong>{order.participantsCount || "-"}</strong>
                  </span>
                </p>
              )}
              {order.comment && (
                <p className={styles.orderDetail}>
                  <Icon name="comment" />
                  <span>
                    {t("PAGES.ORDER_FOOD.CHECKOUT.COMMENT.LABEL")}:{" "}
                    <i style={{ color: "var(--wlcm-color-blue)" }}>
                      "{order.comment}"
                    </i>
                  </span>
                </p>
              )}
            </div>
            {!!order.editHistory?.length && (
              <OrderHistory order={order} menuItems={menu?.items} defaultOpen />
            )}
          </div>
        </div>
        {!editMode && (
          <Button
            icon="print"
            style={{ alignSelf: "end" }}
            onClick={() => handlePrint()}
          >
            {t("SNACK_ADMIN.ORDER_DETAILS.ACTIONS.PRINT")}
          </Button>
        )}
      </div>
      {editMode && (
        <SaveResetButtons
          form={form}
          showButtons={hasUnsavedChanges}
          disabledSubmit={!hasUnsavedChanges}
          hideResetButton
        />
      )}
      <PopupCard onClose={() => setShowDenyModal(false)} open={showDenyModal}>
        <h3>{t("SNACK_ADMIN.ORDER_DETAILS.DENY_MODAL.TITLE")}</h3>
        <p style={{ marginBottom: "var(--wlcm-spacing-sm)" }}>
          {t("SNACK_ADMIN.ORDER_DETAILS.DENY_MODAL.BODY")}
        </p>
        <InputField
          dark
          required
          label={t("SNACK_ADMIN.ORDER_DETAILS.DENY_MODAL.REASON_LABEL")}
          placeholder={t(
            "SNACK_ADMIN.ORDER_DETAILS.DENY_MODAL.REASON_PLACEHOLDER",
          )}
          value={denyReason}
          onChange={(e) => setDenyReason(e.target.value)}
          maxLength={150}
          infoText={t(
            "SNACK_ADMIN.ORDER_DETAILS.DENY_MODAL.CUSTOMER_PREFERRED_LANGUAGE",
            {
              language: t(`LANG_${(order.userLanguage ?? "no").toUpperCase()}`),
            },
          )}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "var(--wlcm-spacing-xs) var(--wlcm-spacing-sm)",
            marginTop: "var(--wlcm-spacing-sm)",
          }}
        >
          <Button dark onClick={() => setShowDenyModal(false)}>
            {t("CANCEL")}
          </Button>
          <Button
            danger
            disabled={!denyReason.trim().length}
            onClick={() => {
              denyOrder({ statusNotes: denyReason });
              setShowDenyModal(false);
              setDenyReason("");
            }}
          >
            {t(
              `SNACK_ADMIN.ORDER_DETAILS.ACTIONS.${order.status.current === SnackOrderStatus.ORDERED ? "DENY" : "CANCEL"}`,
            )}
          </Button>
        </div>
      </PopupCard>
      {/* Hidden printable content */}
      <div style={{ display: "none" }}>
        <PrintableOrders
          ref={printComponentRef}
          orders={printableOrder}
          supplier={supplier}
        />
      </div>
    </>
  );
}
